import { apiRequest } from '../client';
import type { PageResponse } from '../types';

/** Mirrors backend in.voltpassport.quotation.api.QuotationStatus */
export type QuotationStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';

export interface QuotationView {
  id: string;
  code: string;
  /** Nullable for legacy rows created before customer master shipped. */
  customerId: string | null;
  /** Snapshot at time of quote. */
  customerCode: string;
  /** Snapshot at time of quote. */
  customerName: string;
  /** Serialized as string over the wire to preserve precision — parse when doing math. */
  totalAmount: string;
  currency: string;
  validUntil: string;
  status: QuotationStatus;
  createdAt: string;
}

export interface QuotationSummary {
  id: string;
  code: string;
  customerCode: string;
  totalAmount: string;
  currency: string;
  status: QuotationStatus;
}

/**
 * Post-Wave-1 shape: `code` is server-generated (auto-numbered) and customer
 * identity is a FK. Customer code + name are snapshotted server-side.
 */
export interface CreateQuotationRequest {
  customerId: string;
  totalAmount: string;
  currency: string;
  validUntil: string;
}

export interface TransitionQuotationRequest {
  next: QuotationStatus;
}

/**
 * State machine — what statuses a quotation can transition to from its current
 * status. Used by the detail page to render only the valid action buttons.
 * Mirrors the backend's Quotation aggregate transitions.
 */
export const QUOTATION_TRANSITIONS: Record<QuotationStatus, QuotationStatus[]> = {
  DRAFT: ['SENT'],
  SENT: ['ACCEPTED', 'REJECTED', 'EXPIRED'],
  ACCEPTED: [],
  REJECTED: [],
  EXPIRED: []
};

export const quotationsApi = {
  list: (page = 0, size = 20) =>
    apiRequest<PageResponse<QuotationView>>(
      `/api/quotations?page=${page}&size=${size}`
    ),

  summaries: (page = 0, size = 20) =>
    apiRequest<PageResponse<QuotationSummary>>(
      `/api/quotations/summaries?page=${page}&size=${size}`
    ),

  get: (id: string) => apiRequest<QuotationView>(`/api/quotations/${id}`),

  create: (request: CreateQuotationRequest) =>
    apiRequest<QuotationView>('/api/quotations', {
      method: 'POST',
      body: request
    }),

  transition: (id: string, next: QuotationStatus) =>
    apiRequest<QuotationView>(`/api/quotations/${id}/transitions`, {
      method: 'POST',
      body: { next } satisfies TransitionQuotationRequest
    })
};
